import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { ordersApi, type OrderSupportRequestCommentAttachment } from "@/lib/api";
import { formatFileSize } from "./supportRequest";

interface AttachmentDownloadButtonProps {
  orderId: string;
  zendeskTicketId: number;
  attachment: OrderSupportRequestCommentAttachment;
}

/** Baixa um anexo do chamado pela API (com o token do usuário); não dá para usar um link direto para o Zendesk. */
export const AttachmentDownloadButton = ({ orderId, zendeskTicketId, attachment }: AttachmentDownloadButtonProps) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const blob = await ordersApi.downloadRequestCommentAttachment(orderId, zendeskTicketId, attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar anexo do chamado:", error);
      toast.error("Não foi possível baixar o anexo.", { description: "Tente novamente em instantes." });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleDownload}
      disabled={isDownloading}
      aria-label={`Baixar ${attachment.fileName}`}
      className="h-8 gap-2 text-[13px]"
    >
      {isDownloading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      <span className="max-w-[220px] truncate">{attachment.fileName}</span>
      {attachment.size != null && <span className="text-muted-foreground">{formatFileSize(attachment.size)}</span>}
    </Button>
  );
};
